import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import {
  actionButtonColumn,
  createMenuEntry,
  gridColumn,
  gridDateColumn,
  gridFormattedStrongColumn,
  MngTabulatorComponent,
  MngTabulatorViewComponent
} from '@mng/mng-tabulator';
import { ILaborProtokollInfo, ILaborProtokollParams, IPartnerInfo } from '@mng/mgs-lib';
import { LaborProtokollService } from './labor-protokoll.service';
import { CellComponent, ColumnDefinition, RowComponent, RowContextMenuSignature } from 'tabulator-tables';
import {
  InputFieldBooleanComponent,
  InputFieldDateRangeComponent,
  InputFieldSelectComponent,
  InputFieldStringClearableComponent,
  IOption,
  MngDialogService,
  UiGroup
} from '@mng/mng-inputs';
import { MGS_COPY_ICON, MGS_DOCUMENT_ICON, MGS_EDIT_DOCUMENT_ICON, MGS_PDF_ICON } from '../../mgs-svg-icon.helper';
import { ISvgIcons } from '@mng/mng-icon';
import { ActivatedRoute, Router } from '@angular/router';
import { openDownloadWithFilename, writeToClipboard } from '@mng/mng-ts-helpers';
import { MngMgsPartnerAuswahlComponent } from '../../partner/partner-auswahl/partner-auswahl.component';
import { MngRouteHelperService } from '@mng/mng-base-ui';
import { DIENSTLEISTUNG_PATH } from '../../mgs-path-defs';
import { PartnerAuswahlService } from '../../partner/partner-auswahl/partner-auswahl.service';
import { AuthStateFacade } from '../../auth/auth-state.facade';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';

@Component({
  selector: 'mng-mgs-laborprotokoll',
  imports: [
    MatCard,
    MatCardContent,
    MngTabulatorComponent,
    MatButton,
    MngMgsPartnerAuswahlComponent,
    InputFieldBooleanComponent,
    InputFieldSelectComponent,
    InputFieldDateRangeComponent,
    InputFieldStringClearableComponent
  ],
  templateUrl: './labor-protokoll.component.html',
  styleUrl: './labor-protokoll.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LaborProtokollComponent extends MngTabulatorViewComponent<ILaborProtokollInfo> {
  private readonly icons: ISvgIcons = inject(ActivatedRoute).snapshot.data['icons'];
  private readonly router = inject(Router);
  private readonly dialogService = inject(MngDialogService);

  private readonly routeHelperService = inject(MngRouteHelperService);

  heuteDatum = new Date();
  laborUi = new LaborUi(this.service.laborProtokollParams(), this.service.statiOptions());

  readonly partnerAuswahlService = inject(PartnerAuswahlService);

  readonly isAdmin = inject(AuthStateFacade).isAdmin;
  readonly isArzt = inject(AuthStateFacade).isArzt;

  constructor(public service: LaborProtokollService) {
    super({ data: service.protokolle, heightOffset: '215px' });
    if (this.isAdmin()) {
      this.partnerAuswahlService.updatePartnerIdRouteParamsWithSelection();
    }

    this.routeHelperService.updateRouteWithQueryParams(this.service.laborProtokollParams());
  }

  createColumnDef(): ColumnDefinition[] {
    return [
      // Button um Befund zu "bearbeiten" (Admin oder Arzt)
      /// Button um DL zu aktivieren (wenn DL nicht inaktiv und "managed")
      actionButtonColumn(
        this.icons[MGS_EDIT_DOCUMENT_ICON],
        (cell: CellComponent) => this.bearbeiten(this.getDataForCell(cell)),
        'In die Detailansicht wechseln'
      ),

      actionButtonColumn(
        this.icons[MGS_DOCUMENT_ICON],
        (cell: CellComponent) =>
          this.router.navigate([DIENSTLEISTUNG_PATH, this.getDataForCell(cell).dienstleistungsCode]),
        'Zur Dienstleistung wechseln',
        (cell: CellComponent) => !this.getDataForCell(cell).dienstleistungsCode
      ),
      actionButtonColumn(
        this.icons[MGS_PDF_ICON],
        (cell: CellComponent) => this.pdfFuerLaborDatenErmitteln(cell.getRow()),
        'Daten des Laborprotokoll als PDF anzeigen'
      ),
      gridDateColumn('Übern. Datum', 'date', 170, 'center', 'datetime'),
      ...(!this.service.authState.isArzt() ? [gridColumn('Arzt', 'arztInfo', 300)] : []),
      gridColumn('Kunde', 'kundenInfo', 300),
      gridFormattedStrongColumn(gridColumn('Code', 'dienstleistungsCode', 150)),
      gridColumn('Status', 'status.label', 150),
      gridColumn('Untersuchungsergebnisse', 'laborDaten', 380)
    ];
  }

  createRowContextMenu(): RowContextMenuSignature {
    return [
      createMenuEntry(
        (row) => `Code in die Zwischenablage kopieren (${this.getDataForRow(row).dienstleistungsCode})`,
        (e, row) => writeToClipboard(`${this.getDataForRow(row).dienstleistungsCode}`),
        this.icons[MGS_COPY_ICON]
      ),

      ...(this.isAdmin()
        ? [
            createMenuEntry(
              (row) => `ID in die Zwischenablage kopieren (${this.getDataForRow(row).id})`,
              (e, row) => writeToClipboard(`${this.getDataForRow(row).id}`),
              this.icons[MGS_COPY_ICON]
            )
          ]
        : [])
    ];
  }

  bearbeiten(item: ILaborProtokollInfo) {
    void this.router.navigate(['laborprotokolldetail'], { queryParams: { id: item.id } });
  }

  aktualisieren() {
    const params = this.laborUi.getUpdatedData();

    if (this.isAdmin() && !params.partnerId && !params.code) {
      this.dialogService.showOK({ htmlContent: 'Partner muss ausgewählt oder ein Code eingegeben werden!' });
      return;
    }

    void this.service.laborProtokolleErmitteln(this.laborUi.getUpdatedData());
    this.routeHelperService.updateRouteWithQueryParams(params);
  }

  async pdfFuerLaborDatenErmitteln(row: RowComponent) {
    const laborProtokoll = row.getData() as ILaborProtokollInfo;
    try {
      const objectUrl = await this.service.webApi.laborProtokollPdf(laborProtokoll.id);
      openDownloadWithFilename(objectUrl.filename, objectUrl.blob);
    } catch (e) {
      console.error(e);
    }
  }

  onPartnerSelection(partnerInfo: IPartnerInfo) {
    this.laborUi.field('partnerId').control.setValue(partnerInfo?.id ?? undefined);
  }
}

class LaborUi extends UiGroup<Partial<ILaborProtokollParams>> {
  constructor(params: ILaborProtokollParams, statusOptions: IOption[]) {
    super(params);

    this.addField('statusId', +params.statusId, 'Status').withOptions(statusOptions);
    this.addField('code', params.code, 'Code');
    this.addField('vonDatum', params.vonDatum, 'von');
    this.addField('bisDatum', params.bisDatum, 'bis');
    this.addField('partnerId', params?.partnerId, 'Partnerauswahl');
    this.addField('allePartnerUntersuchungen', params.allePartnerUntersuchungen, 'Alle Untersuchungen');
  }
}
